import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";
import type { AgentMaster, AgentMasterUpsertPayload } from "../types";

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* ignore */
  }
  return text || `Request failed (${res.status})`;
}

function normalizeAgent(row: AgentMaster): AgentMaster {
  const pm = row.passwordMode === "custom" ? "custom" : "random";
  return {
    ...row,
    hasPassword: Boolean(row.hasPassword),
    passwordMode: pm,
    onboardingEmailSentAt: row.onboardingEmailSentAt ?? null,
  };
}

export async function fetchAgents(): Promise<AgentMaster[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/agents`);
  if (!res.ok) throw new Error(await parseError(res));
  const rows = (await res.json()) as AgentMaster[];
  return rows.map(normalizeAgent);
}

export async function createAgentApi(row: AgentMasterUpsertPayload): Promise<AgentMaster> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/agents`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(row),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const created = (await res.json()) as AgentMaster;
  return normalizeAgent(created);
}

export async function updateAgentApi(id: string, patch: Partial<AgentMasterUpsertPayload>): Promise<AgentMaster> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/agents/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const updated = (await res.json()) as AgentMaster;
  return normalizeAgent(updated);
}

export async function deleteAgentApi(id: string): Promise<void> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/agents/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await parseError(res));
}

export async function sendAgentOnboardingEmailApi(
  id: string,
  options?: { customPasswordForEmail?: string },
): Promise<AgentMaster> {
  const body: { customPasswordForEmail?: string } = {};
  if (options?.customPasswordForEmail !== undefined && options.customPasswordForEmail !== "") {
    body.customPasswordForEmail = options.customPasswordForEmail;
  }
  const res = await apiFetch(`${getApiBaseUrl()}/api/agents/${encodeURIComponent(id)}/send-onboarding-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const agent = (await res.json()) as AgentMaster;
  return normalizeAgent(agent);
}
